/**
 * "Accept winnings" toast — a notification that the BE already sent a Steam
 * trade offer to the winner. Opened by the BE push `offer_sent` only (BE owner,
 * 2026-10-26: the toast is NOT derived from game state or from a win modal
 * closing — the push is the only trigger). Live on the BE since 2026-10-31.
 *
 * Contract (documented once, in `docs/integration/guide/shared-contract.md`
 * → "`offer_sent` — a Steam trade offer was created"):
 *  `{ userid, game, hash, value, skins, offerid, gameid, roundid?, potid? }`
 *  - `userid` must equal the session steamid, otherwise the push is dropped
 *    (the BE also emits it for bot winners, `userid: '-1'`);
 *  - `game: "Store"` is a store withdrawal → `WithdrawSentModal`, not a toast
 *    (replaces the retired store-withdrawal push, see the contract page);
 *  - the shown total is always Σ `skins[].price`; the BE `value` is ignored.
 *  The payload is parsed in one place: `win-toast-push.port.ts`.
 *
 * The toast UI, queue (`WinToastService`), global container and the socket
 * listener (`WinToastPushListener`) are always mounted. "Accept Winnings" is a
 * link to `https://steamcommunity.com/tradeoffer/<offerid>/` — no FE → BE
 * emit. Toasts auto-dismiss after `WIN_TOAST_AUTO_DISMISS_MS` ms (hover pauses
 * the timer). The dev launcher at `/dev/skins` imitates the push locally
 * (`dev:win_toast`, see `core/dev/dev-mock-skins.ts`).
 */
export const WIN_TOAST_PUSH_EVENT: string | null = 'offer_sent';

/** Maximum number of win toasts stacked at once (newest first). */
export const WIN_TOAST_MAX_STACK = 3;

/** How many recent toast ids are remembered to avoid re-showing a win. */
export const WIN_TOAST_SEEN_KEYS_MAX = 50;

/** Milliseconds before a toast auto-dismisses (hover pauses the timer). */
export const WIN_TOAST_AUTO_DISMISS_MS = 30_000;

/**
 * Games the toast can be shown for. Values are `GAMES_CONFIG[].link`, which
 * gives the badge its icon and label. The BE sends `"Coinflip"`, `"Upgrader"`,
 * `"King"`, `"Jackpot"` (compared lowercased, see `GAME_ALIASES` in the port);
 * jackpot is confirmed by the first real sample, king is unconfirmed but
 * harmless.
 */
export const WIN_TOAST_GAMES = ['coinflip', 'upgrader', 'king', 'jackpot'] as const;
export type WinToastGame = (typeof WIN_TOAST_GAMES)[number];
